import usStates from '@/utils/usStates';

export const SOLAR_BY_STATE_PREFIX = '/solar-by-state/';

export type SolarByStatePageType = 'state' | 'city' | 'branch' | 'other';

export interface ParsedSolarByStatePath {
  pageType: SolarByStatePageType;
  stateCode: string;
  stateName: string;
  cityName?: string;
  slug?: string;
}

/**
 * Slugs containing these tokens are informational / non-market pages.
 * Prefer no schema over inventing a city name.
 */
const EXCLUDED_SLUG_KEYWORDS = [
  'financing',
  'finance',
  'incentive',
  'tax-credit',
  'tax-incentives',
  'county',
  'net-metering',
  'export-rate',
  'understanding-',
  'plans-and-services',
  'battery-storage',
] as const;

/**
 * City names containing these tokens are almost always parse residue, not places.
 */
const INVALID_CITY_TOKENS = [
  'solar',
  'panel',
  'panels',
  'home',
  'installation',
  'financing',
  'finance',
  'incentive',
  'incentives',
  'credit',
  'credits',
  'plan',
  'plans',
  'service',
  'services',
  'branch',
  'local',
] as const;

const BRANCH_SLUG_SUFFIX = '-solar-local-branch';

const META_TITLE_PREFIXES = [
  'Solar Panel Installation in ',
  'Solar Panels ',
  'Solar in ',
] as const;

function formatSlugToTitle(slug: string): string {
  return slug
    .split('-')
    .filter(Boolean)
    .map((word) => {
      if (word === 'of') return 'of';
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');
}

function stateNameToSlug(stateName: string): string {
  return stateName.toLowerCase().replace(/\s+/g, '-');
}

function isExcludedSlug(slug: string): boolean {
  return EXCLUDED_SLUG_KEYWORDS.some((keyword) => slug.includes(keyword));
}

function isValidCityName(cityName: string | undefined): cityName is string {
  if (!cityName) return false;

  const normalized = cityName.trim();
  if (normalized.length < 2) return false;

  // Reject metaTitle leftovers like "Plans and Services in Florida"
  if (/\b(in|and|for|with|about)\b/i.test(normalized)) return false;

  const tokens = normalized.toLowerCase().split(/\s+/);
  if (
    tokens.some((token) =>
      (INVALID_CITY_TOKENS as readonly string[]).includes(token)
    )
  ) {
    return false;
  }

  return true;
}

function parseCityFromMetaTitle(metaTitle: string): string | undefined {
  const withoutSuffix = metaTitle.split('|')[0]?.trim();
  if (!withoutSuffix) return undefined;

  for (const prefix of META_TITLE_PREFIXES) {
    if (!withoutSuffix.startsWith(prefix)) continue;

    const city = withoutSuffix.slice(prefix.length).trim();
    return isValidCityName(city) ? city : undefined;
  }

  return undefined;
}

function isStateOnlySlug(
  slug: string,
  stateName: string,
  stateCode: string
): boolean {
  const stateSlug = stateNameToSlug(stateName);
  const stateCodeLower = stateCode.toLowerCase();

  return (
    slug === stateSlug ||
    slug === stateCodeLower ||
    slug === `${stateSlug}-solar` ||
    slug === `solar-${stateSlug}` ||
    slug === `${stateSlug}-solar-panels` ||
    slug === `${stateCodeLower}-solar` ||
    slug === `${stateCodeLower}-solar-panels`
  );
}

/**
 * Longest-first suffixes that can wrap a city slug.
 * Order matters: more specific patterns must win.
 */
function getCitySlugSuffixes(
  stateName: string,
  stateCode: string
): readonly string[] {
  const stateSlug = stateNameToSlug(stateName);
  const stateCodeLower = stateCode.toLowerCase();

  return [
    `-${stateSlug}-solar-panels`,
    `-${stateSlug}-solar`,
    `-${stateCodeLower}-solar-panels`,
    `-${stateCodeLower}-solar`,
    '-home-solar-panels',
    '-home-solar',
    '-solar-panels',
    '-solar',
    `-${stateSlug}`,
    `-${stateCodeLower}`,
  ];
}

function parseCityNameFromSlug(
  slug: string,
  stateName: string,
  stateCode: string
): string | undefined {
  if (isStateOnlySlug(slug, stateName, stateCode)) {
    return undefined;
  }

  for (const suffix of getCitySlugSuffixes(stateName, stateCode)) {
    if (!slug.endsWith(suffix) || slug.length <= suffix.length) continue;

    const citySlug = slug.slice(0, -suffix.length);
    const cityName = formatSlugToTitle(citySlug);
    if (isValidCityName(cityName)) {
      return cityName;
    }
  }

  // Bare single-token city slug: /il/chicago
  if (!slug.includes('-')) {
    const cityName = formatSlugToTitle(slug);
    return isValidCityName(cityName) ? cityName : undefined;
  }

  return undefined;
}

function parseBranchCityFromSlug(slug: string): string | undefined {
  if (!slug.endsWith(BRANCH_SLUG_SUFFIX)) return undefined;
  const citySlug = slug.slice(0, -BRANCH_SLUG_SUFFIX.length);
  const cityName = citySlug ? formatSlugToTitle(citySlug) : undefined;
  return isValidCityName(cityName) ? cityName : undefined;
}

export function parseSolarByStatePath(
  path: string,
  metaTitle?: string
): ParsedSolarByStatePath | null {
  const normalizedPath =
    path.endsWith('/') && path.length > 1 ? path.slice(0, -1) : path;

  if (!normalizedPath.startsWith(SOLAR_BY_STATE_PREFIX)) {
    return null;
  }

  const segments = normalizedPath.split('/').filter(Boolean);
  // Expect: ['solar-by-state', '{state}'] or ['solar-by-state', '{state}', '{slug}']
  // Nested paths like /fl/es/miami-solar-panels or /dc/washington-dc/solar-finance
  // are too ambiguous — skip rather than invent a location.
  if (
    segments.length < 2 ||
    segments.length > 3 ||
    segments[0] !== 'solar-by-state'
  ) {
    return null;
  }

  const stateCode = segments[1]?.toUpperCase();
  if (!stateCode || !usStates[stateCode]) {
    return null;
  }

  const stateName = usStates[stateCode];
  const slug = segments[2];

  if (!slug) {
    return { pageType: 'state', stateCode, stateName };
  }

  if (isExcludedSlug(slug)) {
    return { pageType: 'other', stateCode, stateName, slug };
  }

  if (slug.endsWith(BRANCH_SLUG_SUFFIX)) {
    const cityName =
      parseBranchCityFromSlug(slug) ??
      (metaTitle ? parseCityFromMetaTitle(metaTitle) : undefined);

    // Branch pages without a trustworthy city name should not emit LocalBusiness.
    if (!isValidCityName(cityName)) {
      return { pageType: 'other', stateCode, stateName, slug };
    }

    return {
      pageType: 'branch',
      stateCode,
      stateName,
      cityName,
      slug,
    };
  }

  const cityNameFromSlug = parseCityNameFromSlug(slug, stateName, stateCode);
  if (cityNameFromSlug) {
    return {
      pageType: 'city',
      stateCode,
      stateName,
      cityName: cityNameFromSlug,
      slug,
    };
  }

  const cityNameFromMetaTitle = metaTitle
    ? parseCityFromMetaTitle(metaTitle)
    : undefined;
  if (cityNameFromMetaTitle) {
    return {
      pageType: 'city',
      stateCode,
      stateName,
      cityName: cityNameFromMetaTitle,
      slug,
    };
  }

  return { pageType: 'other', stateCode, stateName, slug };
}
